import PropTypes from "prop-types";
import React, { useState } from "react";
import DonutChart from "./DonutChart";

import calendarIcon from "../../assets/images/user-dashboard/calendar.svg";

/* -------------------------------------------------------------------------
 * SettlementCard — current settlement period breakdown (owed / owed-to-you
 * line items + net estimate) alongside a donut chart with legend.
 * Supports interactive chart with clickable legend segments.
 * ---------------------------------------------------------------------- */

const SettlementCard = ({ periodLabel, lineItems, net, note, chartSegments, totalMemberSpend, estimatedNetRevenue, status }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleSegmentClick = (index) => {
    setSelectedIndex(selectedIndex === index ? null : index);
  };

  const handleLegendClick = (index) => {
    setSelectedIndex(selectedIndex === index ? null : index);
  };

  return (
  <div className="rp-card">
    <h2 className="rp-card__title">Current Settlement Period</h2>
    <p className="rp-card__subtitle">{periodLabel}</p>

    <div className="rp-settlement">
      <div className="rp-settlement__lines">
        {lineItems.map((item) => (
          <div key={item.label} className="rp-settlement__line">
            <span>{item.label}</span>
            <span className={item.amount >= 0 ? "rp-amount--positive" : "rp-amount--negative"}>
              {item.display || (item.amount >= 0 ? "+" : "-") + Math.abs(item.amount).toFixed(2)}
            </span>
          </div>
        ))}

        <div className="rp-divider" />

        <div className="rp-settlement__line rp-settlement__line--net">
          <span>{net.label}</span>
          <span className={net.amount >= 0 ? "rp-amount--positive" : "rp-amount--negative"}>
            {net.display || (net.amount >= 0 ? "+" : "-") + Math.abs(net.amount).toFixed(2)}
          </span>
        </div>

        {totalMemberSpend && (
          <div className="rp-settlement__line">
            <span>Total Member Spend</span>
            <span className="rp-amount--positive">{totalMemberSpend}</span>
          </div>
        )}

        {estimatedNetRevenue && (
          <div className="rp-settlement__line">
            <span>Est. Net Revenue</span>
            <span className="rp-amount--positive">{estimatedNetRevenue}</span>
          </div>
        )}

        <div className="rp-settlement__note">
          <img src={calendarIcon} alt="" />
          <span>{note}</span>
        </div>

        {status && (
          <div className="rp-settlement__status">
            <span className={`rp-badge ${status === 'settlement_completed' ? 'rp-badge--returning' : 'rp-badge--new'}`}>
              {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
        )}
      </div>

      <div className="rp-settlement__chart">
        <DonutChart 
          segments={chartSegments} 
          onSegmentClick={handleSegmentClick}
          selectedIndex={selectedIndex}
        />
        <div className="rp-settlement__legend">
          {chartSegments.map((seg, index) => (
            <div 
              key={seg.label} 
              className={`rp-settlement__legend-row ${selectedIndex === index ? 'rp-settlement__legend-row--selected' : ''}`}
              onClick={() => handleLegendClick(index)}
              style={{ cursor: 'pointer', opacity: selectedIndex !== null && selectedIndex !== index ? 0.5 : 1 }}
            >
              <span className="rp-settlement__legend-dot" style={{ background: seg.color }} />
              <span className="rp-settlement__legend-label">{seg.label}</span>
              <span
                className={seg.value >= 0 ? "rp-amount--positive" : "rp-amount--negative"}
              >
                {seg.value >= 0 ? "+" : "-"}${Math.abs(seg.value).toFixed(2)}
              </span>
            </div>
          ))}
          <div className="rp-settlement__legend-row rp-settlement__legend-row--net">
            <span className="rp-settlement__legend-label">Net</span>
            <span className={net.amount >= 0 ? "rp-amount--positive" : "rp-amount--negative"}>
              {net.display || (net.amount >= 0 ? "+" : "-") + Math.abs(net.amount).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

SettlementCard.propTypes = {
  periodLabel: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, amount: PropTypes.number.isRequired })
  ).isRequired,
  net: PropTypes.shape({ label: PropTypes.string.isRequired, amount: PropTypes.number.isRequired })
    .isRequired,
  note: PropTypes.string.isRequired,
  chartSegments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ).isRequired,
  totalMemberSpend: PropTypes.string,
  estimatedNetRevenue: PropTypes.string,
  status: PropTypes.string,
};

export default SettlementCard;
